import ReactDOM from "react-dom";
import modalStyles from "../modals.module.scss";
import taskModalStyles from "./EditTaskModal.module.scss";
import {
  createTask,
  updateTask,
  patchTask,
  fetchCategoryUsers,
  fetchTaskComments,
  createComment,
  patchComment,
  deleteComment,
  patchTaskUserRelation,
  deleteTaskUserRelation,
  createTaskUserRelation,
  deleteTaskUserRelations,
  uploadTaskFile,
  deleteTaskFile,
  deleteOffer,
  createOffer,
  fetchClients,
  patchOffer,
  deleteTask,
  fetchUserCategories,
} from "../../../api";
import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../../../AppContext";
import UserImage from "../../UserImage";
import CustomAutocomplete from "../../CustomAutocomplete";
import {
  symmetricDifference,
  symmetricDifferenceByID,
} from "../../../utils/arrayUtils";
import { CommentInputBox } from "./CommentInputBox";
import { CommentBox } from "./CommentBox";
import { images_path } from "../../../constants";
import TextArea from "../../TextComponents/TextArea";
import ConfirmationBoxModal from "../ConfirmationBoxModal";

function EditTaskModal({ isOpen, onClose, onUpdate, editTask = null }) {
  //#region Fields
  const { user, userCategories, statusChoices, statusDict } =
    useContext(AppContext);

  const [invalidName, setInvalidName] = useState(false);

  const [currentTask, setCurrentTask] = useState(null);
  const [taskName, setTaskName] = useState("");
  const [taskWorkflow, setTaskWorkflow] = useState("");
  const [taskCategory, setTaskCategory] = useState(-1);
  const [taskStatus, setTaskStatus] = useState(statusChoices[0].value);
  const [taskDescription, setTaskDescription] = useState("");
  const [taskFiles, setTaskFiles] = useState([]);
  const [taskUsers, setTaskUsers] = useState([]);
  const [categoryUsers, setCategoryUsers] = useState([]);
  const [hoursEstimated, setHoursEstimated] = useState(0);

  const [comments, setComments] = useState([]);

  const [commentText, setCommentText] = useState("");
  const [editCommentText, setEditCommentText] = useState("");
  const [editCommentId, setEditCommentId] = useState(-1);

  const [clients, setClients] = useState([]);
  const [offer, setOffer] = useState(null);

  const [offerName, setOfferName] = useState("");
  const [offerClient, setOfferClient] = useState(null);
  const [offerClientWorkName, setOfferClientWorkName] = useState("");
  const [offerClientRepresentative, setOfferClientRepresentative] =
    useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const justOpened = useRef(true);

  //#endregion

  //#region Functions

  const initTaskName = (taskId) => {
    fetchUserCategories().then((res) => {
      const currentCategory = res.find(
        (category) => category.id === Number(taskId),
      );
      if (!currentCategory || !currentCategory.prefix) {
        return;
      }
      let newTaskName = currentCategory.prefix;
      newTaskName += currentCategory.has_trailing_whitespace ? " " : "";
      if (currentCategory.is_progressive) {
        newTaskName += currentCategory.next_number;
      }
      if (taskName.startsWith(newTaskName)) {
        return;
      }
      handleSetTaskName(newTaskName);
    });
  };

  const handleSetTaskCategory = (value) => {
    setTaskCategory(value);
    patchTask(editTask.id, { category: value }).then((res) => {
      if (!res) {
        return;
      }

      initTaskName(value);
    });
  };

  const handleSetTaskName = (value) => {
    setTaskName(value);
    patchTask(editTask.id, { name: value }).then((res) => {
      if (!res) {
        setInvalidName(true);
      } else {
        setInvalidName(false);
      }
    });
  };

  const handleSetTaskWorkflow = (value) => {
    setTaskWorkflow(value);
    patchTask(editTask.id, { workflow: value });
  };

  const handleDelete = async () => {
    await deleteTask(editTask.id);
    closeWindow();
  };

  const closeWindow = () => {
    justOpened.current = false;
    onClose();
  };

  const handleAddTaskUser = async (newUser) => {
    setTaskUsers([...taskUsers, newUser]);

    const data = {
      task: editTask.id,
      user: newUser.id,
      hours_estimated: 0,
    };

    await createTaskUserRelation(data);
  };

  const handleEditComment = () => {
    const commentData = {
      content: editCommentText,
    };

    patchComment(editCommentId, commentData).then(() => {
      fetchTaskComments(editTask.id).then((comments) => {
        setComments(comments);
        setEditCommentId(-1);
        setEditCommentText("");
      });
    });
  };

  const handleDeleteComment = (commentId) => {
    deleteComment(commentId).then(() => {
      fetchTaskComments(editTask.id).then((comments) => {
        setComments(comments);
      });
    });
  };

  const handleAddComment = async () => {
    const commentData = {
      task: editTask.id,
      user: user.id,
      content: commentText,
    };
    await createComment(commentData);
    setCommentText("");
    fetchTaskComments(editTask.id).then((comments) => {
      setComments(comments);
    });
  };

  const getUserData = () => {
    const userData = [];
    taskUsers.map((taskUser) => {
      const hours =
        taskUser.id.toString() === user.id.toString() ? hoursEstimated : 0;
      const data = {
        user: Number(taskUser.id),
        hours_estimated: Number(hours),
      };
      userData.push(data);
    });
    return userData;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const taskData = {
      user_data: getUserData(),
      name: taskName,
      description: taskDescription,
      completed: false,
      category: Number(taskCategory),
    };

    const res = await updateTask(editTask.id, taskData);

    if (res) {
      onUpdate();
      closeWindow();
    }
  };

  const removeTaskUser = async (userId) => {
    setTaskUsers(taskUsers.filter((user) => user.id !== userId));
    await deleteTaskUserRelation(editTask.id, userId);
  };

  const setInitialData = async () => {
    if (!editTask) return;

    if (editTask.name) setInvalidName(false);
    else initTaskName(editTask.category.id);

    setCurrentTask(editTask);
    setTaskName(
      editTask.name
        ? editTask.name
        : editTask.category.prefix + editTask.category.has_trailing_whitespace
          ? " "
          : "",
    );
    setTaskWorkflow(editTask.workflow ? editTask.workflow : "");
    setTaskCategory(editTask.category.id);
    setTaskStatus(editTask.status);
    setTaskDescription(editTask.description ? editTask.description : "");
    setTaskFiles(editTask.files);
    setTaskUsers(() => {
      return editTask.assigned_users.length > 0
        ? editTask.assigned_users.map((user) => {
            return user;
          })
        : [];
    });

    const filteredUserData = editTask.user_data.filter(
      (data) => data.user === user.id,
    );
    setHoursEstimated(editTask.hours_estimated);

    fetchTaskComments(editTask.id).then((comments) => {
      setComments(comments);
    });

    setEditCommentId(-1);
    setEditCommentText("");

    if (editTask.offer) {
      setOffer(editTask.offer);
      setOfferName(editTask.offer.name ? editTask.offer.name : "");
      setOfferClient(editTask.offer.client);
      setOfferClientWorkName(
        editTask.offer.client_work_name ? editTask.offer.client_work_name : "",
      );
      setOfferClientRepresentative(
        editTask.offer.client_representative
          ? editTask.offer.client_representative
          : null,
      );
    } else {
      setOffer(null);
      setOfferName("");
      setOfferClient(null);
      setOfferClientWorkName("");
      setOfferClientRepresentative(null);
    }

    fetchClients().then((clients) => {
      setClients(clients);
    });
  };

  //#endregion

  useEffect(() => {
    const handleCloseKeyDown = (event) => {
      if (event.key === "Escape") {
        closeWindow();
      }
    };

    document.addEventListener("keydown", handleCloseKeyDown);
    setInitialData();
    return document.removeEventListener("keydown", handleCloseKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!editTask) return;

    fetchCategoryUsers(taskCategory).then((categoryUsers) => {
      setCategoryUsers(categoryUsers);
      // Remove  task users that are not category users
      const newTaskUsers = taskUsers.filter((taskUser) =>
        categoryUsers.some((categoryUser) => categoryUser.id === taskUser.id),
      );
      const removedTaskUsers = symmetricDifferenceByID(taskUsers, newTaskUsers);
      setTaskUsers(newTaskUsers);
      if (removedTaskUsers.length > 0) {
        deleteTaskUserRelations(
          editTask.id,
          removedTaskUsers.map((user) => user.id),
        );
      }
    });
  }, [taskCategory]);

  useEffect(() => {
    if (justOpened.current) {
      justOpened.current = false;
      return;
    }
  }, []);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className={taskModalStyles.task_modal_overlay}>
      <div className={taskModalStyles.task_modal_content_container}>
        <div className={taskModalStyles.task_modal_content}>
          <button
            title="Chiudi"
            className={taskModalStyles.esc_btn}
            onClick={closeWindow}
          >
            <img
              className={taskModalStyles.esc_btn_icon}
              src={images_path + "x_bold.svg"}
            ></img>
          </button>
          <h1>Dati Commessa</h1>
          <div className={taskModalStyles.title_div}>
            <span style={{ marginRight: "0.5rem" }}>Nome:</span>
            <div className={taskModalStyles.name_input_div}>
              {invalidName && (
                <span className={taskModalStyles.abs_text}>
                  *Nome già presente
                </span>
              )}
              <input
                className={invalidName ? "invalid" : ""}
                placeholder="Commessa"
                value={taskName}
                onChange={(e) => {
                  handleSetTaskName(e.target.value);
                }}
              ></input>
            </div>
            <span style={{ marginLeft: "1.5rem", marginRight: "0.5rem" }}>
              Workflow:
            </span>
            <input
              name="workflow"
              id="worfklow"
              value={taskWorkflow}
              onChange={(e) => {
                handleSetTaskWorkflow(e.target.value);
              }}
            ></input>
            <span style={{ marginLeft: "1.5rem", marginRight: "0.5rem" }}>
              Categoria:
            </span>
            <select
              name="categories"
              id="categories"
              value={taskCategory}
              onChange={(e) => {
                handleSetTaskCategory(e.target.value);
              }}
            >
              {userCategories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            <span style={{ marginLeft: "1.5rem", marginRight: "0.5rem" }}>
              Status:
            </span>
            <select
              name="status"
              id="status"
              value={taskStatus}
              onChange={(e) => {
                setTaskStatus(e.target.value);
                patchTask(editTask.id, { status: e.target.value });
              }}
            >
              {statusChoices.map((statusChoice, idx) => (
                <option key={idx} value={statusChoice.value}>
                  {statusDict[statusChoice.value]}
                </option>
              ))}
            </select>
          </div>
          <div className={taskModalStyles.description_div}>
            <label className={taskModalStyles.title_label}>Descrizione</label>
            <TextArea
              value={taskDescription}
              attachments={taskFiles}
              onChange={(value) => {
                setTaskDescription(value);
              }}
              onSave={() =>
                patchTask(editTask.id, { description: taskDescription })
              }
              onAttach={(file) => {
                uploadTaskFile(editTask.id, file).then((res) => {
                  if (res) {
                    setTaskFiles([...taskFiles, res]);
                  }
                });
              }}
              onUnattach={(file) => {
                deleteTaskFile(file.id);
                setTaskFiles(taskFiles.filter((f) => f.id !== file.id));
              }}
              placeholder=""
            />
          </div>
          <div className={taskModalStyles.members_div}>
            <span className={taskModalStyles.title_label}>Membri</span>
            <div className={taskModalStyles.user_div}>
              {taskUsers.map((taskUser) => {
                return (
                  <div
                    key={taskUser.id}
                    className={taskModalStyles.task_user_hours_div}
                  >
                    <div className={taskModalStyles.task_user_div}>
                      <UserImage targetUser={taskUser} />
                      <label style={{ fontWeight: "500" }}>
                        {taskUser.first_name + " " + taskUser.last_name}
                      </label>
                      <button
                        className={taskModalStyles.x_button}
                        onClick={() => removeTaskUser(taskUser.id)}
                      >
                        <img src={images_path + "x_thin.svg"}></img>
                      </button>
                    </div>
                    <span
                      onClick={() => {
                        console.log(
                          currentTask.user_data.filter(
                            (data) => data.user == 1,
                          )[0].hours_worked,
                        );
                      }}
                    >
                      Ore totali:{" "}
                      {currentTask.user_data.filter(
                        (data) => data.user == taskUser.id,
                      )[0]?.hours_worked
                        ? String(
                            currentTask.user_data.filter(
                              (data) => data.user == taskUser.id,
                            )[0]?.hours_worked,
                          )
                        : 0}
                    </span>
                  </div>
                );
              })}
              Ore totali complessive:{" "}
              {currentTask?.total_hours_worked
                ? currentTask?.total_hours_worked
                : 0}
              <CustomAutocomplete
                options={symmetricDifferenceByID(
                  categoryUsers.filter((user) => user.worker),
                  taskUsers.filter((user) => user.worker),
                )}
                optionLabel={(option) => {
                  return option.first_name + " " + option.last_name;
                }}
                optionDisplay={(option) => {
                  return (
                    <div className={taskModalStyles.option}>
                      <UserImage targetUser={option} />
                      {option.first_name + " " + option.last_name}
                    </div>
                  );
                }}
                onSelect={(newValue) => handleAddTaskUser(newValue)}
              />
              {!taskUsers.some((taskUser) => taskUser.id === user.id) && (
                <button
                  onClick={() => handleAddTaskUser(user)}
                  className="button"
                >
                  Unisciti
                </button>
              )}
            </div>
          </div>
          <span className={taskModalStyles.label_input_div}>
            Ore stimate:
            <input
              type="number"
              min="0"
              max="1000"
              style={{ maxWidth: "5rem" }}
              id="hours"
              value={hoursEstimated}
              onChange={(e) => {
                setHoursEstimated(e.target.value);
                patchTask(editTask.id, {
                  hours_estimated: e.target.value,
                });
              }}
            ></input>
          </span>
          {!offer ? (
            <button
              onClick={() => {
                createOffer({ task: editTask.id }).then((res) => {
                  setOffer(res);
                });
              }}
              className="button"
            >
              Aggiungi cliente
            </button>
          ) : (
            <button
              onClick={() => {
                deleteOffer(offer.id).then(() => {
                  setOffer(null);
                });
              }}
              className="button"
            >
              Rimuovi cliente
            </button>
          )}
          {offer && (
            <>
              <span className={taskModalStyles.title_label}>Dati cliente</span>
              <div className={taskModalStyles.task_modal_content_container}>
                <div className={taskModalStyles.label_input_div}>
                  <label>Cliente:</label>
                  <select
                    value={offerClient?.id || ""}
                    onChange={(e) => {
                      setOfferClient(
                        clients.find((client) => client.id == e.target.value),
                      );
                      patchOffer(offer.id, {
                        client_id: e.target.value,
                      }).then((res) => {});
                    }}
                  >
                    <option key={-1} value={""}>
                      ---
                    </option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className={taskModalStyles.label_input_div}>
                  <label>Commessa cliente:</label>
                  <input
                    value={offerClientWorkName}
                    onChange={(e) => {
                      setOfferClientWorkName(e.target.value);
                      patchOffer(offer.id, {
                        client_work_name: e.target.value,
                      });
                    }}
                  ></input>
                </div>
                {offerClient?.representatives && (
                  <div className={taskModalStyles.label_input_div}>
                    <label>Referente cliente:</label>
                    <select
                      value={offerClientRepresentative || ""}
                      onChange={(e) => {
                        setOfferClientRepresentative(e.target.value);
                        patchOffer(offer.id, {
                          client_representative: e.target.value,
                        });
                      }}
                    >
                      <option key={-1} value={""}>
                        ---
                      </option>
                      {offerClient?.representatives.map((representative) => (
                        <option
                          key={representative.id}
                          value={representative.id}
                        >
                          {representative.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div className={taskModalStyles.label_input_div}>
                  <label>Offerta:</label>
                  <input
                    value={offerName}
                    onChange={(e) => {
                      setOfferName(e.target.value);
                      patchOffer(offer.id, {
                        name: e.target.value,
                      });
                    }}
                  ></input>
                </div>
              </div>
            </>
          )}
          <div className={taskModalStyles.hr_div}>
            <hr className={taskModalStyles.hr}></hr>
            Attività
            <hr className={taskModalStyles.hr}></hr>
          </div>
          <div className={taskModalStyles.comments_div}>
            <CommentInputBox
              onChange={(value) => setCommentText(value)}
              onSubmit={handleAddComment}
              text={commentText}
            />
            {comments.map((comment) => {
              return editCommentId === comment.id ? (
                <CommentInputBox
                  onChange={(value) => setEditCommentText(value)}
                  onSubmit={handleEditComment}
                  onCancel={() => setEditCommentId(-1)}
                  text={editCommentText}
                  key={comment.id}
                />
              ) : (
                <CommentBox
                  comment={comment}
                  onEdit={() => {
                    setEditCommentId(comment.id);
                    setEditCommentText(comment.content);
                  }}
                  onDelete={() => handleDeleteComment(comment.id)}
                  key={comment.id}
                />
              );
            })}
          </div>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <button
              className={taskModalStyles.delete_btn + " button_var1"}
              onClick={() => {
                setIsDeleteModalOpen(true);
              }}
            >
              Elimina
            </button>
          </div>
        </div>
      </div>
      <ConfirmationBoxModal
        text="Eliminare commessa?"
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
        }}
        onConfirm={handleDelete}
      />
    </div>,
    document.getElementById("modal-root"),
  );
}

export default EditTaskModal;
